import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, CheckCircle2, User, Plus } from "lucide-react";
import { NHSEmergencyMatchedPatient } from "@/lib/nhs111/nhs111Data";

// MUI Skeletons
import Skeleton from "@mui/material/Skeleton";

interface PatientMatchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  matchPatient: { name: string; consultationId: string } | null; // Added proper type
  onMatch: (patientId: string) => void;
  isDarkMode?: boolean;
  NHSEmergencyPatientData: NHSEmergencyMatchedPatient[];
  isNHSEmergencyPatientLoading: boolean;
  handleAddNewPatient: () => void;
}

export function PatientMatchDialog({
  open,
  onOpenChange,
  onMatch,
  isDarkMode = false,
  matchPatient, // Now properly destructured
  NHSEmergencyPatientData,
  isNHSEmergencyPatientLoading,
  handleAddNewPatient,
}: PatientMatchDialogProps) {
  const [searchTerm, setSearchTerm] = useState("");

  const filteredMatches = searchTerm
    ? NHSEmergencyPatientData.filter(
        (match) =>
          match?.patientName
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          match?.nhsNumber?.includes(searchTerm) ||
          match?.address?.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    : NHSEmergencyPatientData;

  const getMatchScoreColor = (score: number) => {
    if (score >= 90)
      return isDarkMode
        ? "bg-green-900/30 text-green-300 border-green-800"
        : "bg-green-50 text-green-700 border-green-200";
    if (score >= 75)
      return isDarkMode
        ? "bg-yellow-900/30 text-yellow-300 border-yellow-800"
        : "bg-yellow-50 text-yellow-700 border-yellow-200";
    return isDarkMode
      ? "bg-orange-900/30 text-orange-300 border-orange-800"
      : "bg-orange-50 text-orange-700 border-orange-200";
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={`max-w-3xl max-h-[80vh] overflow-hidden flex flex-col ${
          isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white"
        }`}
      >
        {/* HEADER */}
        <DialogHeader>
          {isNHSEmergencyPatientLoading ? (
            <>
              <Skeleton height={28} width={260} />
              <Skeleton height={20} width={180} />
            </>
          ) : (
            <>
              <DialogTitle
                className={isDarkMode ? "text-white" : "text-gray-900"}
              >
                Match Patient with Local Record
              </DialogTitle>
              <DialogDescription
                className={isDarkMode ? "text-gray-400" : "text-gray-600"}
              >
                Patient:{" "}
                <span className="font-semibold">
                  {matchPatient?.name || "Unknown"}
                </span>
              </DialogDescription>
            </>
          )}
        </DialogHeader>

        {/* BODY */}
        <div className="flex flex-col flex-1 space-y-4 overflow-hidden">
          {/* SEARCH BAR */}
          <div className="flex gap-2">
            {isNHSEmergencyPatientLoading ? (
              <Skeleton height={40} width="100%" />
            ) : (
              <div className="relative flex-1">
                <Search
                  className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                />
                <Input
                  placeholder="Search by name, NHS number, or address..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`pl-10 focus-visible:ring-0 focus-visible:ring-offset-0 ${
                    isDarkMode
                      ? "bg-gray-700 border-gray-600 text-white placeholder:text-gray-400"
                      : "bg-white border-gray-200"
                  }`}
                />
              </div>
            )}
          </div>

          {isNHSEmergencyPatientLoading ? (
            <Skeleton height={20} width={150} />
          ) : (
            <h3
              className={`text-sm font-medium ${
                isDarkMode ? "text-gray-300" : "text-gray-700"
              }`}
            >
              Suggested Matches ({filteredMatches.length})
            </h3>
          )}

          <div className="flex-1 space-y-2 overflow-y-auto">
            {isNHSEmergencyPatientLoading ? (
              <>
                {Array.from({ length: 4 }).map((_, idx) => (
                  <div key={idx} className="p-4 space-y-3 border rounded-lg">
                    <Skeleton height={25} width="40%" />
                    <Skeleton height={18} width="60%" />
                    <Skeleton height={18} width="50%" />
                    <Skeleton height={18} width="75%" />
                    <Skeleton height={36} width={90} />
                  </div>
                ))}
              </>
            ) : filteredMatches.length === 0 ? (
              <div
                className={`text-center py-8 ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                <User className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm">No matching patients found</p>
                <p className="mt-1 text-xs">
                  Try adjusting your search criteria
                </p>

                <div className="pt-3 mt-3 border-gray-200">
                  <Button
                    onClick={() => handleAddNewPatient()}
                    className="text-sm text-white bg-blue-600 hover:bg-blue-700"
                    size="sm"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add New Patient
                  </Button>
                  <p className="mt-2 text-xs opacity-75">
                    Can't find the patient? Add them to the system's
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredMatches.map((match) => (
                  <div
                    key={match._id}
                    className={`p-4 rounded-lg border transition-all ${
                      isDarkMode
                        ? "bg-gray-700 border-gray-600 hover:bg-gray-650"
                        : "bg-white border-gray-200 hover:border-gray-300 hover:shadow-md"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-2">
                          <h4
                            className={`font-medium ${
                              isDarkMode ? "text-white" : "text-gray-900"
                            }`}
                          >
                            {match.patientName}
                          </h4>
                          <Badge
                            className={`text-xs px-2 py-0.5 border ${getMatchScoreColor(
                              match.matchScore,
                            )}`}
                          >
                            {match.matchScore}% match
                          </Badge>
                        </div>

                        <div
                          className={`grid grid-cols-2 gap-2 text-sm ${
                            isDarkMode ? "text-gray-300" : "text-gray-600"
                          }`}
                        >
                          <div>
                            <span
                              className={
                                isDarkMode ? "text-gray-400" : "text-gray-500"
                              }
                            >
                              DOB:
                            </span>{" "}
                            {new Date(match.dob).toLocaleDateString("en-GB")}
                          </div>
                          <div>
                            <span
                              className={
                                isDarkMode ? "text-gray-400" : "text-gray-500"
                              }
                            >
                              NHS Number:
                            </span>{" "}
                            {match.nhsNumber}
                          </div>
                          <div className="col-span-2">
                            <span
                              className={
                                isDarkMode ? "text-gray-400" : "text-gray-500"
                              }
                            >
                              Address:
                            </span>{" "}
                            {match.address}
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-1.5">
                          {match.matchReasons.map((reason, idx) => (
                            <Badge
                              key={idx}
                              variant="outline"
                              className={`text-xs ${
                                isDarkMode
                                  ? "bg-blue-900/20 text-blue-300 border-blue-800"
                                  : "bg-blue-50 text-blue-700 border-blue-200"
                              }`}
                            >
                              {reason}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      <Button
                        onClick={() => {
                          onMatch(match.patientID);
                          onOpenChange(false);
                        }}
                        size="sm"
                        className={
                          isDarkMode
                            ? "bg-green-700 hover:bg-green-600 text-white"
                            : "bg-green-600 hover:bg-green-700 text-white"
                        }
                      >
                        <CheckCircle2 className="w-4 h-4 mr-1" />
                        Match
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div
            className={`pt-4 border-t ${
              isDarkMode ? "border-gray-700" : "border-gray-200"
            }`}
          >
            {isNHSEmergencyPatientLoading ? (
              <Skeleton height={42} width="100%" />
            ) : (
              <Button
                variant="outline"
                className={`w-full ${
                  isDarkMode
                    ? "bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
                    : ""
                }`}
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
